import {create} from "zustand"
import type {DocumentStatus, DocumentType, OtkBlank, OtkDestructionAct} from "shared"

import {getOtkDocumentById} from "@/lib/api/documents"
import {getOtkBlanksPage} from "@/lib/api/warehouse"
import {submitDestructionAct as postDestructionAct} from "@/lib/api/destructionActs"
import {destructionActSubmitErrorMessage, pendingActFromError} from "@/lib/destructionAct"
import {
  type WarehouseTabStatus,
  warehouseTabStatuses,
  warehouseTabToBackendStatus,
  type WarehouseTypeFilter,
  warehouseTypeFilterToBackend,
} from "@/lib/warehouse"

interface TabState {
  blanks: OtkBlank[]
  cursor: string | null
  hasMore: boolean
  isLoading: boolean
  isLoadingMore: boolean
  error: string | null
  /** The type filter this bucket's rows were fetched under — `fetchTab` refetches from page 1 whenever the requested filter no longer matches, since the real endpoint filters server-side rather than this client narrowing an already-fetched page. */
  fetchedType: WarehouseTypeFilter | null
}

function emptyTabState(): TabState {
  return {
    blanks: [],
    cursor: null,
    hasMore: false,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    fetchedType: null,
  }
}

function initialByStatus(): Record<WarehouseTabStatus, TabState> {
  return Object.fromEntries(
    warehouseTabStatuses.map((status) => [status, emptyTabState()])
  ) as Record<WarehouseTabStatus, TabState>
}

function errorMessage(err: unknown): string {
  console.error(err)
  return "Не вдалося завантажити склад спецпродукції."
}

/** A linked document's fields `annulActionAvailability` needs — fetched lazily, one real `GET /otk/documents/:id` per distinct id, and cached here for the session. */
export type DocumentSummary = {
  status: DocumentStatus
  type: DocumentType
  signedAt: string | null
}

interface WarehouseState {
  byStatus: Record<WarehouseTabStatus, TabState>
  fetchTab: (status: WarehouseTabStatus, type: WarehouseTypeFilter) => Promise<void>
  loadMore: (status: WarehouseTabStatus, type: WarehouseTypeFilter) => Promise<void>
  // «Використані»'s row action needs its linked document's real status/type/
  // signedAt to run `annulActionAvailability` — `OtkBlank` doesn't embed
  // them, so this fetches each distinct `documentId` once (real
  // `GET /otk/documents/:id`, `lib/api/documents.ts`'s existing
  // `getOtkDocumentById`) and caches the result here rather than leaving the
  // gate unimplemented for want of a blanks-list field.
  documentSummaries: Record<string, DocumentSummary | "loading" | "error">
  fetchDocumentSummary: (documentId: string) => Promise<void>
  // «Анулювати документ» — local-only optimistic update (moves the row's
  // status to ANNULLED), same "no backend endpoint yet" precedent
  // `store/documents.ts`'s own `annul` already documents: `PATCH`ing the
  // real document's status has no endpoint yet, so only this client's own
  // blank-list view of the annulment is recorded.
  annulDocument: (blankId: string, reason: string) => void
  // The canonical destruction procedure (warehouse.md's "Процедура
  // знищення"), now backed by the real
  // `POST /otk/destruction-acts/sign` (otk-destruction-acts.ts): the caller has
  // already signed the exact JSON payload with the user's КЕП (see
  // `DestructionActDialog`); this posts those JSON bytes in the body and the
  // signature in X-KEP-Signature. Claimed blanks stay in their source tabs
  // while submission is pending and move to «Знищені» after acceptance. A
  // failed first registry attempt still resolves `ok: true` with the
  // persisted `SUBMISSION_PENDING` act (customer decision 30.09.2026: the
  // act is kept for a manual retry), so the caller can offer that retry.
  submitDestructionAct: (
    signedPayload: string,
    kepSignature: string
  ) => Promise<{ ok: true; act: OtkDestructionAct } | { ok: false; message: string }>
  // Projects an act's current state onto the loaded tabs — used by
  // `submitDestructionAct` and by `ActViewDialog`'s manual retry.
  applyDestructionAct: (act: OtkDestructionAct) => void
}

async function loadTab(
  status: WarehouseTabStatus,
  type: WarehouseTypeFilter,
  set: (fn: (state: WarehouseState) => Partial<WarehouseState>) => void
) {
  set((state) => ({
    byStatus: {
      ...state.byStatus,
      [status]: { ...state.byStatus[status], isLoading: true, error: null },
    },
  }))
  try {
    const page = await getOtkBlanksPage({
      status: warehouseTabToBackendStatus[status],
      type: warehouseTypeFilterToBackend[type],
    })
    set((state) => ({
      byStatus: {
        ...state.byStatus,
        [status]: {
          ...state.byStatus[status],
          blanks: page.data,
          cursor: page.nextPage,
          hasMore: page.nextPage !== null,
          isLoading: false,
          fetchedType: type,
        },
      },
    }))
  } catch (err) {
    set((state) => ({
      byStatus: {
        ...state.byStatus,
        [status]: { ...state.byStatus[status], isLoading: false, error: errorMessage(err) },
      },
    }))
  }
}

export const useWarehouseStore = create<WarehouseState>((set, get) => ({
  byStatus: initialByStatus(),
  documentSummaries: {},

  fetchDocumentSummary: async (documentId) => {
    if (get().documentSummaries[documentId]) return
    set((state) => ({
      documentSummaries: { ...state.documentSummaries, [documentId]: "loading" },
    }))
    try {
      const detail = await getOtkDocumentById(documentId)
      set((state) => ({
        documentSummaries: {
          ...state.documentSummaries,
          [documentId]: {
            status: detail.status,
            type: detail.type,
            signedAt: detail.signedAt ?? null,
          },
        },
      }))
    } catch (err) {
      console.error(err)
      set((state) => ({
        documentSummaries: { ...state.documentSummaries, [documentId]: "error" },
      }))
    }
  },

  fetchTab: async (status, type) => {
    const current = get().byStatus[status]
    if ((current.fetchedType === type && current.fetchedType !== null) || current.isLoading) {
      return
    }
    await loadTab(status, type, set)
  },

  loadMore: async (status, type) => {
    const current = get().byStatus[status]
    if (!current.hasMore || current.isLoadingMore || !current.cursor) return
    set((state) => ({
      byStatus: { ...state.byStatus, [status]: { ...current, isLoadingMore: true } },
    }))
    try {
      const page = await getOtkBlanksPage({
        cursor: current.cursor,
        status: warehouseTabToBackendStatus[status],
        type: warehouseTypeFilterToBackend[type],
      })
      set((state) => ({
        byStatus: {
          ...state.byStatus,
          [status]: {
            ...state.byStatus[status],
            blanks: [...state.byStatus[status].blanks, ...page.data],
            cursor: page.nextPage,
            hasMore: page.nextPage !== null,
            isLoadingMore: false,
          },
        },
      }))
    } catch (err) {
      set((state) => ({
        byStatus: {
          ...state.byStatus,
          [status]: { ...state.byStatus[status], isLoadingMore: false, error: errorMessage(err) },
        },
      }))
    }
  },

  // Simplification vs. warehouse.md's full П5 cascade: the real annulment
  // moves the *document* to АНУЛЬОВАНО and, only when a *separate* linked
  // physical blank exists, cascades that sibling row ВИКОРИСТАНО →
  // АНУЛЬОВАНО while the electronic unit stays ВИКОРИСТАНО. `OtkBlank` has
  // no electronic/physical sibling pairing exposed (only its own
  // `documentId`), so there is no way to tell which *other* row, if any,
  // should cascade — this moves only the clicked row itself.
  annulDocument: (blankId, reason) =>
    set((state) => {
      const usedTab = state.byStatus.used
      const blank = usedTab.blanks.find((b) => b.id === blankId)
      if (!blank) return state
      // The reason text the user typed into `AnnulDialog` — warehouse.md's
      // own `annulLinkedPhysicalBlank` cascade writes this into the newly
      // `ANNULLED` blank's `statusReason` ("Примітка" column, «Анульовані»).
      const annulled: OtkBlank = { ...blank, status: "ANNULLED", statusReason: reason }
      return {
        byStatus: {
          ...state.byStatus,
          used: {
            ...usedTab,
            blanks: usedTab.blanks.filter((b) => b.id !== blankId),
          },
          annulled: {
            ...state.byStatus.annulled,
            blanks: [annulled, ...state.byStatus.annulled.blanks],
          },
        },
      }
    }),

  submitDestructionAct: async (signedPayload, kepSignature) => {
    let act: OtkDestructionAct
    try {
      act = await postDestructionAct(signedPayload, kepSignature)
    } catch (err) {
      const pending = pendingActFromError(err)
      if (!pending) return { ok: false, message: destructionActSubmitErrorMessage(err) }
      act = pending
    }
    get().applyDestructionAct(act)
    return { ok: true, act }
  },

  applyDestructionAct: (act) => {
    const idSet = new Set(act.items.map((item) => item.blankId))
    const sourceTabs = ["available", "annulled"] as const
    set((current) => {
      if (act.status === "SUBMISSION_PENDING") {
        return {
          byStatus: {
            ...current.byStatus,
            available: {
              ...current.byStatus.available,
              blanks: current.byStatus.available.blanks.map((b) =>
                idSet.has(b.id) ? { ...b, destructionActId: act.id } : b
              ),
            },
            annulled: {
              ...current.byStatus.annulled,
              blanks: current.byStatus.annulled.blanks.map((b) =>
                idSet.has(b.id) ? { ...b, destructionActId: act.id } : b
              ),
            },
          },
        }
      }

      const destroyedRows = sourceTabs.flatMap((tab) =>
        current.byStatus[tab].blanks.filter((b) => idSet.has(b.id))
      )
      const destroyed: OtkBlank[] = destroyedRows.map((b) => ({
        ...b,
        status: "DESTROYED",
        statusReason: act.blankReasons[b.id] ?? null,
        destructionActId: act.id,
        destructionActNumber: act.actNumber,
        updatedAt: act.acceptedAt ?? act.createdAt,
      }))
      return {
        byStatus: {
          ...current.byStatus,
          available: {
            ...current.byStatus.available,
            blanks: current.byStatus.available.blanks.filter((b) => !idSet.has(b.id)),
          },
          annulled: {
            ...current.byStatus.annulled,
            blanks: current.byStatus.annulled.blanks.filter((b) => !idSet.has(b.id)),
          },
          destroyed: {
            ...current.byStatus.destroyed,
            blanks: [...destroyed, ...current.byStatus.destroyed.blanks],
          },
        },
      }
    })
  },
}))
